import { useEffect, useState } from "react";
import React from "react";
import Floor from "../../images/Stair";
import Door from "../../images/Door";
import { FloorPlanDetail } from "../../services/floorPlan.service";
import CloseIcon from "../../images/CloseIcon";
import EditIconV2 from "../../images/EditIconV2";
import DuplicateIcon from "../../images/DuplicateIcon";
import ElementIcon from "../../images/ElementIcon";
import Stair from "../../images/Stair";
import BarCounter from "../../images/BarCounter";
import LeftSideBarCounter from "../../images/LeftSideBarCounter";
import RightSideBarCounter from "../../images/RightSideBarCounter";
import ShortStair from "../../images/ShortStair";
import LeftSideDoor from "../../images/LeftSideDoor";
import RightSideDoor from "../../images/RightSideDoor";
import VerticalPartition from "../../images/VerticalPartition";
import HorizontallPartition from "../../images/HorizontallPartition";
import Toilet from "../../images/Toilet";
import Kitchen from "../../images/Kitchen";
import { useResponsive } from "../../hook/useResponsive";

interface PopupDrawFloorPlanProps {
    onClose: () => void;
    onSave: (shape: Shape[]) => void;
    listData: FloorPlanDetail[];
}

interface Point {
    x: number;
    y: number;
}

export interface ImageShape {
    id: number;
    type: "image";
    x: number;
    y: number;
    width: number;
    height: number;
    component?: JSX.Element;
    name: string;
    shape?: string;
    rotation?: number;
    radius?: number;
}

type Shape = FloorPlanDetail | ImageShape;
type ResizeDir = "tl" | "tr" | "bl" | "br" | null;

const DrawFloorPlan: React.FC<PopupDrawFloorPlanProps> = ({ onClose, listData, onSave }) => {
    const path = window.location.pathname;
    const id: string = path.split('/').pop() || '';
    const [showConfig, setShowConfig] = useState(false);
    const [showImageDeleteConfirm, setShowImageDeleteConfirm] = useState(false);
    const [imageToDelete, setImageToDelete] = useState<number | null>(null);

    const widgetSizes: { [key: number]: { width: number; height: number } } = {
        1: { width: 248, height: 137 },
        2: { width: 200, height: 150 },
        3: { width: 200, height: 150 },
        4: { width: 200, height: 263 },
        5: { width: 180, height: 200 },
        6: { width: 248, height: 137 },
        7: { width: 248, height: 137 },
        8: { width: 248, height: 137 },
        9: { width: 150, height: 200 },
        10: { width: 200, height: 150 },
        11: { width: 150, height: 150 },
        12: { width: 200, height: 200 },
    };

    const listWidget = [
        {
            id: 1,
            name: "Bar counter",
            component: <BarCounter />,
        },
        {
            id: 2,
            name: "Left-sided bar counter",
            component: <LeftSideBarCounter />,
        },
        {
            id: 3,
            name: "Right-sided bar counter",
            component: <RightSideBarCounter />,
        },
        {
            id: 4,
            name: "Stairs",
            component: <Stair />,
        },
        {
            id: 5,
            name: "Short stairs",
            component: <ShortStair />,
        },
        {
            id: 6,
            name: "Left-sided door",
            component: <Door />,
        },
        {
            id: 7,
            name: "Double doors",
            component: <LeftSideDoor />,
        },
        {
            id: 8,
            name: "Right-sided door",
            component: <RightSideDoor />,
        },
        {
            id: 9,
            name: "Vertical partition",
            component: <VerticalPartition />,
        },
        {
            id: 10,
            name: "Horizontal partition",
            component: <HorizontallPartition />,
        },
        {
            id: 11,
            name: "Toilet",
            component: <Toilet />,
        },
        {
            id: 12,
            name: "Kitchen",
            component: <Kitchen />,
        },
    ];
    const { isMobile, getWidth } = useResponsive();
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [showWidget, setShowWidget] = useState(false);
    const [drawing, setDrawing] = useState(false);
    const [duplicate, setDuplicate] = useState(false);
    const [language, setLanguage] = useState<any>(null);
    const [start, setStart] = useState<Point | null>(null);
    const [current, setCurrent] = useState<Point | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [dragging, setDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<Point>({ x: 0, y: 0 });
    const [resizing, setResizing] = useState<ResizeDir>(null);
    const [errors, setErrors] = useState<any>({});
    const [dragStartPos, setDragStartPos] = useState<Point | null>(null);
    const containerW = window.innerWidth - 32;
    const containerH = window.innerHeight - 32;
    const [valueConfig, setValueConfig] = useState<{
        name: string;
        position: string | number;
        minCovers: string | number;
        maxCovers: string | number;
        shapeType: string;
        radius: number;
        rotation: number;
    }>({
        name: "",
        position: "",
        minCovers: "",
        maxCovers: "",
        shapeType: "rectangle",
        radius: 0,
        rotation: 0,
    });

    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    useEffect(() => {
        if (id !== "new" && listData.length > 0) {
            const widgetMap = Object.fromEntries(
                listWidget.map(w => [w.name, w.component])
            ) as Record<string, JSX.Element>;

            const formatData = listData.map((item) => {
                return {
                    ...item,
                    component: widgetMap[item.name],
                };
            });
            setShapes(formatData);
        }
    }, [id, listData]);

    const handleSelectWidget = (widget: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const size = widgetSizes[widget.id] || { width: 200, height: 200 };
        setShowWidget(false);

        const canvas = document.querySelector('.canvas-container');
        if (canvas) {
            const bounds = canvas.getBoundingClientRect();
            const mouseX = e.clientX - bounds.left;
            const mouseY = e.clientY - bounds.top;

            const newImage: ImageShape = {
                id: Date.now(),
                type: "image",
                x: Math.max(0, Math.min(mouseX - size.width / 2, containerW - size.width)),
                y: Math.max(0, Math.min(mouseY - size.height / 2, containerH - size.height)),
                width: size.width,
                height: size.height,
                component: widget.component,
                name: widget.name,
                shape: "image",
                rotation: 0,
                radius: 0,
            };
            setShapes((prev) => [...prev, newImage]);
            setSelectedId(newImage.id);
        }
    };

    const getRelativePoint = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ): Point => {
        const bounds = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        let finalValue: string | number = value;

        if (type === "number") {
            if (value === "") {
                finalValue = "";
            } else {
                let val = Number(value);
                const min =
                    valueConfig?.minCovers !== ""
                        ? Number(valueConfig.minCovers)
                        : undefined;
                const max =
                    valueConfig?.maxCovers !== ""
                        ? Number(valueConfig.maxCovers)
                        : undefined;

                if (name === "minCovers") {
                    if (val < 1) val = 1;
                    if (max !== undefined && val > max) {
                        val = max;
                    }
                    finalValue = val;
                }

                if (name === "maxCovers") {
                    if (val < 1) {
                        val = 1;
                    }
                    if (val > 20) val = 20;
                    if (min !== undefined && val < min) {
                        val = min;
                    }
                    finalValue = val;
                }
            }
        }

        setValueConfig((prev: any) => ({
            ...prev,
            [name]: finalValue,
        }));
    };

    const handleImageClick = (imageId: number, e: React.MouseEvent) => {
        e.stopPropagation();

        // Kiểm tra xem có phải là thao tác click thật sự không (không phải drag)
        if (dragStartPos) {
            const currentPos = { x: e.clientX, y: e.clientY };
            const distance = Math.sqrt(
                Math.pow(currentPos.x - dragStartPos.x, 2) +
                Math.pow(currentPos.y - dragStartPos.y, 2)
            );

            // Nếu khoảng cách di chuyển > 5px thì coi như đang drag, không mở popup
            if (distance > 5) {
                return;
            }
        }

        setImageToDelete(imageId);
        setShowImageDeleteConfirm(true);
    };

    const confirmImageDelete = () => {
        if (imageToDelete !== null) {
            setShapes((prev) => prev.filter((s) => s.id !== imageToDelete));
            setImageToDelete(null);
            setShowImageDeleteConfirm(false);
            if (selectedId === imageToDelete) {
                setSelectedId(null);
            }
        }
    };

    const cancelImageDelete = () => {
        setImageToDelete(null);
        setShowImageDeleteConfirm(false);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const pos = getRelativePoint(e);
        setDragStartPos({ x: e.clientX, y: e.clientY });
        const margin = 16;

        // Kiểm tra resize handles chỉ cho rect shapes
        for (let i = shapes.length - 1; i >= 0; i--) {
            const s = shapes[i];

            if (s.type === "rect") {
                const within = (dx: number, dy: number, px: number, py: number) =>
                    Math.abs(dx - px) < margin && Math.abs(dy - py) < margin;

                if (within(s.x, s.y, pos.x, pos.y)) {
                    setSelectedId(s.id);
                    setResizing("tl");
                    return;
                }
                if (within(s.x + s.width, s.y, pos.x, pos.y)) {
                    setSelectedId(s.id);
                    setResizing("tr");
                    return;
                }
                if (within(s.x, s.y + s.height, pos.x, pos.y)) {
                    setSelectedId(s.id);
                    setResizing("bl");
                    return;
                }
                if (within(s.x + s.width, s.y + s.height, pos.x, pos.y)) {
                    setSelectedId(s.id);
                    setResizing("br");
                    return;
                }
            }
        }

        // Tìm shape được click (bao gồm cả image)
        const shape = shapes
            .slice()
            .reverse()
            .find(
                (s) =>
                    pos.x >= s.x &&
                    pos.x <= s.x + s.width &&
                    pos.y >= s.y &&
                    pos.y <= s.y + s.height
            );

        if (shape) {
            setSelectedId(shape.id);
            setDragging(true);
            setDragOffset({ x: pos.x - shape.x, y: pos.y - shape.y });
        } else {
            setSelectedId(null);
            setStart(pos);
            setCurrent(pos);
            setDrawing(true);
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const pos = getRelativePoint(e);

        if (drawing) {
            setCurrent(pos);
            return;
        }

        if (resizing && selectedId != null) {
            setShapes((prev) =>
                prev.map((s) => {
                    if (s.id !== selectedId || s.type !== "rect") return s;

                    let { x, y, width, height } = s;
                    const minSize = 20;

                    switch (resizing) {
                        case "tl":
                            const newW_tl = width + (x - pos.x);
                            const newH_tl = height + (y - pos.y);
                            if (newW_tl > minSize && newH_tl > minSize) {
                                x = pos.x;
                                y = pos.y;
                                width = newW_tl;
                                height = newH_tl;
                            }
                            break;

                        case "tr":
                            const newW_tr = pos.x - x;
                            const newH_tr = height + (y - pos.y);
                            if (newW_tr > minSize && newH_tr > minSize) {
                                y = pos.y;
                                width = newW_tr;
                                height = newH_tr;
                            }
                            break;

                        case "bl":
                            const newW_bl = width + (x - pos.x);
                            const newH_bl = pos.y - y;
                            if (newW_bl > minSize && newH_bl > minSize) {
                                x = pos.x;
                                width = newW_bl;
                                height = newH_bl;
                            }
                            break;

                        case "br":
                            const newW_br = pos.x - x;
                            const newH_br = pos.y - y;
                            if (newW_br > minSize && newH_br > minSize) {
                                width = newW_br;
                                height = newH_br;
                            }
                            break;
                    }

                    x = Math.max(0, Math.min(x, containerW - width));
                    y = Math.max(0, Math.min(y, containerH - height));

                    return { ...s, x, y, width, height };
                })
            );
            return;
        }

        if (dragging && selectedId != null) {
            setShapes((prev) =>
                prev.map((s) =>
                    s.id === selectedId
                        ? {
                            ...s,
                            x: Math.max(0, Math.min(pos.x - dragOffset.x, containerW - s.width)),
                            y: Math.max(0, Math.min(pos.y - dragOffset.y, containerH - s.height)),
                        }
                        : s
                )
            );
        }
    };

    const handleMouseUp = () => {
        if (drawing && start && current) {
            const newRect: any = {
                id: Date.now(),
                type: "rect",
                x: Math.min(start.x, current.x),
                y: Math.min(start.y, current.y),
                width: Math.abs(current.x - start.x),
                height: Math.abs(current.y - start.y),
                shape: "rectangle",
                name: "",
                radius: 0,
                rotation: 0,
                position: 0,
                maxCovers: 0,
                minCovers: 0,
                isReserved: false,
                reservedFrom: null,
                reservedTo: null,
            };
            if (newRect.width > 20 && newRect.height > 20) {
                setShapes((prev) => [...prev, newRect]);
                setSelectedId(newRect.id);
                setShowConfig(true);
            }
        }
        setDrawing(false);
        setStart(null);
        setCurrent(null);
        setDragging(false);
        setResizing(null);

        // Reset dragStartPos sau một khoảng delay nhỏ để handleImageClick có thể sử dụng
        setTimeout(() => {
            setDragStartPos(null);
        }, 100);
    };

    const handleDelete = () => {
        if (selectedId != null) {
            setShapes((prev) => {
                const shape = prev.find((s) => s.id === selectedId);
                if (shape?.type === "image") return prev;
                return prev.filter((s) => s.id !== selectedId);
            });
            setSelectedId(null);
            setShowConfig(false);
            setValueConfig({
                name: "",
                position: "",
                minCovers: "",
                maxCovers: "",
                shapeType: "rectangle",
                radius: 0,
                rotation: 0,
            });
        }
    };

    const handleSaveConfig = () => {
        setErrors({});
        const shape = shapes.find((s) => s.id === selectedId);
        const newErrors: any = {};
        if (valueConfig.position === "") {
            newErrors.position = 'Table number is required';
        }
        if (valueConfig.name === "") {
            newErrors.name = 'Table name No is required';
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        let finalValue = {
            id: shape?.id || Date.now(),
            name: valueConfig?.name || "Table " + valueConfig?.position,
            type: "rect",
            shape: valueConfig?.shapeType || "rectangle",
            x: shape?.x || 0,
            y: shape?.y || 0,
            width: shape?.width || 0,
            height: shape?.height || 0,
            radius: valueConfig?.shapeType === "circle" || valueConfig?.shapeType === "oval" ? 9999 : (+valueConfig?.radius || 0),
            rotation: valueConfig?.shapeType === "circle" || valueConfig?.shapeType === "oval" ? 1 : (+valueConfig?.rotation || 0),
            position: +valueConfig?.position || 0,
            maxCovers: +valueConfig?.maxCovers || 0,
            minCovers: +valueConfig?.minCovers || 0,
            isReserved: false,
            reservedFrom: null,
            reservedTo: null,
        };
        setShapes((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...finalValue, shape: finalValue.shape as FloorPlanDetail["shape"] } : s
            )
        );
        setShowConfig(false);
        setValueConfig({
            name: "",
            position: "",
            minCovers: "",
            maxCovers: "",
            shapeType: "rectangle",
            radius: 0,
            rotation: 0,
        });
        setErrors({});
    };

    const getShapeOptions = (width: number, height: number) => {
        const isSquare = Math.abs(width - height) < 20;
        if (isSquare) {
            return [
                { value: "square", label: "Square" },
                { value: "circle", label: "Circle" }
            ];
        } else {
            return [
                { value: "rectangle", label: "Rectangle" },
                { value: "oval", label: "Oval" }
            ];
        }
    };

    const renderCircularChairs = (s: any) => {
        const chairs: JSX.Element[] = [];
        const total = s.maxCovers ?? 0;
        if (total <= 0) return chairs;

        const centerX = s.width / 2;
        const centerY = s.height / 2;
        const radius = Math.min(s.width, s.height) / 2 + 20;
        const chairSize = 24;

        for (let i = 0; i < total; i++) {
            const angle = (2 * Math.PI * i) / total - Math.PI / 2;
            const chairX = centerX + radius * Math.cos(angle) - chairSize / 2;
            const chairY = centerY + radius * Math.sin(angle) - chairSize / 2;

            chairs.push(
                <div
                    key={`chair-${i}`}
                    className="absolute"
                    style={{
                        left: chairX,
                        top: chairY,
                        width: chairSize,
                        height: chairSize,
                        backgroundColor: "#EBF0F0",
                        borderRadius: "50%",
                    }}
                />
            );
        }

        return chairs;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            !duplicate ? handleSaveConfig() : handleDuplicate();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [valueConfig]);

    const handleDuplicate = () => {
        setErrors({});
        const shape = shapes.find((s) => s.id === selectedId);
        const newErrors: any = {};
        if (valueConfig.position === "") {
            newErrors.position = 'Table number is required';
        }
        if (valueConfig.name === "") {
            newErrors.name = 'Table name No is required';
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        let finalValue = {
            id: Date.now(),
            name: valueConfig?.name || "Table " + valueConfig?.position,
            type: "rect",
            shape: valueConfig?.shapeType || "rectangle",
            x: (shape?.x || 0) + 60 + (shape?.width || 0),
            y: shape?.y || 0,
            width: shape?.width || 0,
            height: shape?.height || 0,
            radius: valueConfig?.shapeType === "circle" || valueConfig?.shapeType === "oval" ? 9999 : (+valueConfig?.radius || 0),
            rotation: valueConfig?.shapeType === "circle" || valueConfig?.shapeType === "oval" ? 1 : (+valueConfig?.rotation || 0),
            position: +valueConfig?.position || 0,
            maxCovers: +valueConfig?.maxCovers || 0,
            minCovers: +valueConfig?.minCovers || 0,
            isReserved: false,
            reservedFrom: null,
            reservedTo: null,
        };
        setShapes((prev: any) => [...prev, finalValue]);
        setShowConfig(false);
        setValueConfig({
            name: "",
            position: "",
            minCovers: "",
            maxCovers: "",
            shapeType: "rectangle",
            radius: 0,
            rotation: 0,
        });
        setErrors({});
        setDuplicate(false);
    };

    return (
        <div style={{
            borderRadius: "20px", backgroundColor: "white", width: "100%", height: "100%", transition: "transform 0.3s ease, opacity 0.3s ease", overflow: "hidden"
        }}
            className="relative">
            <div style={{ top: '16px', padding: '0px 20px' }} className="absolute top-4 cursor-pointer flex items-center justify-between w-full z-50">
                <p style={{ color: '#222525', fontWeight: "600", fontSize: '20px', lineHeight: "28px" }} className="font-inter">{language && language?.["Organize your workspace"] ? language?.["Organize your workspace"] : "Organize your workspace"}</p>
                <div className="flex items-center" style={{ gap: 16 }} >
                    <div
                        style={{ padding: "10px 24px", borderRadius: '40px', gap: '8px', backgroundColor: '#EBF0F0' }}
                        className="relative flex items-center justify-center w-fit cursor-pointer"
                        onClick={() => { setShowWidget(true) }}
                    >
                        <div className="flex items-center justify-center" style={{ width: '24px', height: "24px" }}>
                            <ElementIcon />
                        </div>
                        <p style={{ color: '#222525', fontWeight: "500" }} className="text-[14px] leading-[20px] text-main font-inter">{language && language?.["Elements"] ? language?.["Elements"] : "Elements"}</p>
                        {
                            showWidget &&
                            <div className="absolute dropdown-elements scroll-container" style={{ right: isMobile ? "-100%" : "0", height: "80vh", minWidth: isMobile ? "89vw" : "532px", overflowY: "scroll", top: "52px", padding: '20px 24px', borderRadius: '20px', border: "1px solid #EBF0F0", backgroundColor: '#FFFFFF' }}>
                                <div className="flex items-center justify-between">
                                    <p style={{ color: '#222525', fontWeight: "600", fontSize: "20px" }} className="text-[14px] leading-[20px] text-main font-inter">{language && language?.["Drag and drop elements"] ? language?.["Drag and drop elements"] : "Drag and drop elements"}</p>
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowWidget(false);
                                        }}
                                        className="cursor-pointer flex items-center justify-center"
                                        style={{
                                            width: "40px",
                                            height: "40px",
                                        }}
                                    >
                                        <CloseIcon />
                                    </div>
                                </div>
                                <div className="flex items-center" style={{ gap: '12px', marginTop: "16px", flexWrap: "wrap" }}>
                                    {listWidget.map((v) => {
                                        return (
                                            <div
                                                key={v.id}
                                                onClick={(e: any) => handleSelectWidget(v, e)}
                                                className="cursor-pointer hover:bg-gray-50 transition-colors"
                                                style={{ padding: '12px', border: '1px solid #DBE4E4', borderRadius: '12px', width: 'calc(50% - 6px)' }}
                                            >
                                                <div className="flex items-center justify-center" style={{ width: '100%', minHeight: "124px" }}>
                                                    {v.component}
                                                </div>
                                                <p style={{ color: '#222525', fontWeight: "500", fontSize: "14px", marginTop: '12px' }} className="text-[14px] leading-[20px] text-main font-inter">{language && language?.[`${v.name}`] ? language?.[`${v.name}`] : `${v.name}`}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        }
                    </div>
                    <div
                        style={{ padding: "10px 24px", border: '1px solid #0F7679', borderRadius: '40px' }}
                        className="flex items-center justify-center w-fit cursor-pointer relative"
                        onClick={() => { onSave(shapes) }}
                    >
                        <p style={{ color: '#0F7679', fontWeight: "500" }} className="text-[14px] leading-[20px] text-main font-inter">{language && language?.["Save"] ? language?.["Save"] : "Save"}</p>
                    </div>
                    <div
                        onClick={(e) => {
                            setShowConfig(false);
                            setValueConfig({
                                name: "",
                                position: "",
                                minCovers: "",
                                maxCovers: "",
                                shapeType: "rectangle",
                                radius: 0,
                                rotation: 0,
                            });
                            onClose();
                        }}
                        className="cursor-pointer"
                        style={{
                            width: "24px",
                            height: "24px",
                        }}
                    >
                        <CloseIcon />
                    </div>
                </div>
            </div>

            {/* Image Delete Confirmation Modal */}
            <div className={`${showImageDeleteConfirm ? "fixed" : "hidden"} inset-0 flex items-center justify-center bg-black/40 z-50`}>
                <div className="bg-white rounded-xl shadow-lg relative" style={{ padding: '24px', borderRadius: "24px", width: "400px" }}>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">{language?.['Remove Image'] ?? 'Remove Image'}</h2>
                        <div
                            onClick={cancelImageDelete}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "100%",
                                backgroundColor: "#D1E4E5",
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <p className="mb-6 text-gray-600">
                        {language?.['Are you sure you want to remove this image from the floor plan?'] ?? 'Are you sure you want to remove this image from the floor plan?'}
                    </p>
                    <div className="flex justify-end" style={{ gap: "12px" }}>
                        <button
                            onClick={cancelImageDelete}
                            type="button"
                            style={{ borderRadius: "40px", padding: "10px 40px", border: "1px solid #C9D7D8" }}
                            className="bg-white text-gray-700"
                        >
                            {language?.['Cancel'] ?? 'Cancel'}
                        </button>
                        <button
                            onClick={confirmImageDelete}
                            type="button"
                            style={{ borderRadius: "40px", padding: "10px 40px", color: "white", backgroundColor: "#BA0E13" }}
                        >
                            {language?.['Remove'] ?? 'Remove'}
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: "#22252552" }} className={`${showConfig ? "fixed" : "hidden"} inset-0 flex items-center justify-center bg-black/40 z-50`}>
                <div className="bg-white rounded-xl shadow-lg relative" style={{ padding: '24px', borderRadius: "24px", width: "500px" }}>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">{language?.['Configure table'] ?? 'Configure table'}</h2>
                        <div
                            onClick={() => {
                                setShowConfig(false);
                                setValueConfig({
                                    name: "",
                                    position: "",
                                    minCovers: "",
                                    maxCovers: "",
                                    shapeType: "rectangle",
                                    radius: 0,
                                    rotation: 0,
                                });
                                setDuplicate(false);
                            }}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: "24px",
                                height: "24px",
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <form className="space-y-4">

                        <div className="space-y-2">
                            <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                {language?.['Table number'] ?? 'Table number'} <span style={{ color: "#BA0E13" }}>*</span>
                            </label>
                            <input
                                type="number"
                                value={valueConfig?.position}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                name="position"
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            {errors.position && <p className="error">{errors.position}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                {language?.['Table name'] ?? 'Table name'}<span style={{ color: "#BA0E13" }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={valueConfig?.name}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                name="name"
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            {errors.name && <p className="error">{errors.name}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                {language?.['Shape Type'] ?? 'Shape Type'}<span style={{ color: "#BA0E13" }}>*</span>
                            </label>
                            <select
                                value={valueConfig?.shapeType}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                name="shapeType"
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            >
                                {(() => {
                                    const shape = shapes.find((s) => s.id === selectedId);
                                    if (shape && shape.type === "rect") {
                                        const options = getShapeOptions(shape.width, shape.height);
                                        return options.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {language?.[option.label] ?? option.label}
                                            </option>
                                        ));
                                    }
                                    return (
                                        <option value="rectangle">{language?.['Rectangle'] ?? 'Rectangle'}</option>
                                    );
                                })()}
                            </select>
                        </div>
                        <div className="flex items-center" style={{ gap: '20px' }}>
                            <div className="space-y-2" style={{ width: 'calc(50% - 10px)' }}>
                                <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                    {language?.['Min Covers'] ?? 'Min Covers'}
                                </label>
                                <input
                                    type="number"
                                    value={valueConfig?.minCovers}
                                    onChange={(e: any) => {
                                        handleChange(e);
                                    }}
                                    min={1}
                                    max={valueConfig?.maxCovers || 1}
                                    name="minCovers"
                                    className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                />
                            </div>
                            <div className="space-y-2" style={{ width: 'calc(50% - 10px)' }}>
                                <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                    {language?.['Max Covers'] ?? 'Max Covers'}
                                </label>
                                <input
                                    type="number"
                                    min={valueConfig?.minCovers || 1}
                                    max={20}
                                    value={valueConfig?.maxCovers}
                                    onChange={(e: any) => {
                                        handleChange(e);
                                    }}
                                    name="maxCovers"
                                    className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                />
                            </div>
                        </div>
                        {valueConfig?.shapeType !== "circle" && valueConfig?.shapeType !== "oval" && (
                            <>
                                <div className="flex items-center" style={{ gap: '20px' }}>
                                    <div className="space-y-2" style={{ width: 'calc(50% - 10px)' }}>
                                        <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                            {language?.['Radius'] ?? 'Radius'}
                                        </label>
                                        <input
                                            type="number"
                                            value={valueConfig?.radius}
                                            onChange={(e: any) => {
                                                handleChange(e);
                                            }}
                                            name="radius"
                                            min={1}
                                            max={50}
                                            className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                        />
                                    </div>
                                    <div className="space-y-2" style={{ width: 'calc(50% - 10px)' }}>
                                        <label className="text-[14px] font-medium" style={{ color: '#4A5353' }}>
                                            {language?.['Rotation '] ?? 'Rotation '} <span style={{ color: '#6A7878' }}> {language?.['(degrees)'] ?? '(degrees)'}</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={valueConfig?.rotation}
                                            onChange={(e: any) => {
                                                handleChange(e);
                                            }}
                                            name="rotation"
                                            min={0}
                                            max={360}
                                            className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="flex justify-end pt-2" style={{ gap: "12px" }}>
                            <button
                                onClick={() => {
                                    setShowConfig(false);
                                    setValueConfig({
                                        name: "",
                                        position: "",
                                        minCovers: "",
                                        maxCovers: "",
                                        shapeType: "rectangle",
                                        radius: 0,
                                        rotation: 0,
                                    });
                                    setDuplicate(false);
                                }}
                                type="button"
                                style={{ borderRadius: "40px", padding: "10px 40px", border: "1px solid #C9D7D8", color: "#222525", fontSize: "14px", fontWeight: '500' }}
                            >
                                {language?.['Cancel'] ?? 'Cancel'}
                            </button>
                            <button
                                className=""
                                onClick={handleDelete}
                                style={{ borderRadius: "40px", padding: "10px 40px", color: "white", backgroundColor: '#BA0E13', fontSize: "14px", fontWeight: '500' }}
                                type="button"
                                disabled={selectedId == null}
                            >
                                {language?.['Delete'] ?? 'Delete'}
                            </button>
                            <button
                                onClick={() => !duplicate ? handleSaveConfig() : handleDuplicate()}
                                type="button"
                                style={{ borderRadius: "40px", padding: "10px 40px", color: "white", backgroundColor: "#0F7679", fontSize: "14px", fontWeight: '500' }}
                            >
                                {language?.['Save'] ?? 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div
                className="relative w-full h-full overflow-hidden canvas-container"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            >
                {shapes.map((s) => (
                    <div
                        key={s.id}
                        className={`absolute ${s.id === selectedId ? "ring-2 ring-red-500" : ""}`}
                        style={{
                            left: s.x,
                            top: s.y,
                            width: s.width,
                            height: s.height,
                            backgroundColor: s.shape !== "image" ? "#EBF0F0" : "transparent",
                            borderRadius: s.shape === "circle" || s.shape === "oval" ? "50%" : s.shape === "image" ? "0px" : s?.radius || 0,
                        }}
                    >
                        {s.type === "rect" && (
                            <>
                                <div className="w-full h-full" />
                                {s.name && (
                                    <div className="absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none", fontSize: "17px", fontWeight: 500, color: "#05292A" }}>
                                        {s.name}
                                    </div>
                                )}
                                <div className="flex items-center absolute" style={{ gap: '8px', top: "10px", right: "10px" }}>
                                    <div
                                        onClick={() => {
                                            setShowConfig(true);
                                            setValueConfig({
                                                ...s,
                                                shapeType: s.shape || "rectangle"
                                            });
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '30px',
                                            backgroundColor: '#FFFFFF',
                                            border: '1px solid #0F7679'
                                        }}
                                        className="font-inter cursor-pointer"
                                    >
                                        <EditIconV2 />
                                    </div>
                                    <div
                                        onClick={() => {
                                            setShowConfig(true);
                                            setSelectedId(s.id);
                                            setDuplicate(true);
                                        }}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '30px',
                                            backgroundColor: '#FFFFFF',
                                            border: '1px solid #0F7679'
                                        }}
                                        className="font-inter cursor-pointer"
                                    >
                                        <DuplicateIcon />
                                    </div>
                                </div>
                                {s.id === selectedId && (
                                    <>
                                        <div className="resize-handle resize-nw" />
                                        <div className="resize-handle resize-ne" />
                                        <div className="resize-handle resize-sw" />
                                        <div className="resize-handle resize-se" />
                                    </>
                                )}

                                {s.shape === "circle" || s.shape === "oval" ? (
                                    renderCircularChairs(s)
                                ) : (
                                    (() => {
                                        const chairs: JSX.Element[] = [];
                                        const total = s.maxCovers ?? 0;
                                        const chairThickness = 10;
                                        const gap = 8;
                                        const spacing = 16;
                                        if (total <= 0) return chairs;

                                        const makeChair = (
                                            key: string | number,
                                            left: number,
                                            top: number,
                                            w: number,
                                            h: number
                                        ) => (
                                            <div
                                                key={key}
                                                className="absolute"
                                                style={{
                                                    left,
                                                    top,
                                                    width: w,
                                                    height: h,
                                                    backgroundColor: "#EBF0F0",
                                                    borderRadius: "16px",
                                                }}
                                            />
                                        );

                                        if (total === 1) {
                                            chairs.push(
                                                makeChair(
                                                    "top-single",
                                                    s.width * 0.1,
                                                    -chairThickness - gap,
                                                    s.width * 0.8,
                                                    chairThickness
                                                )
                                            );
                                            return chairs;
                                        }

                                        if (total <= 3) {
                                            const topCount = Math.ceil(total / 2);
                                            const bottomCount = Math.floor(total / 2);

                                            const drawRow = (count: number, side: "top" | "bottom") => {
                                                if (count === 1) {
                                                    chairs.push(
                                                        makeChair(
                                                            `${side}-0`,
                                                            s.width * 0.1,
                                                            side === "top" ? -chairThickness - gap : s.height + gap,
                                                            s.width * 0.8,
                                                            chairThickness
                                                        )
                                                    );
                                                } else {
                                                    const totalWidth = s.width * 0.8;
                                                    const seatWidth = (totalWidth - spacing * (count - 1)) / count;
                                                    for (let i = 0; i < count; i++) {
                                                        const left = s.width * 0.1 + i * (seatWidth + spacing);
                                                        chairs.push(
                                                            makeChair(
                                                                `${side}-${i}`,
                                                                left,
                                                                side === "top" ? -chairThickness - gap : s.height + gap,
                                                                seatWidth,
                                                                chairThickness
                                                            )
                                                        );
                                                    }
                                                }
                                            };

                                            drawRow(topCount, "top");
                                            drawRow(bottomCount, "bottom");
                                            return chairs;
                                        }

                                        const base = Math.floor(total / 4);
                                        const extra = total % 4;

                                        const topCount = base + (extra > 0 ? 1 : 0);
                                        const bottomCount = base + (extra > 1 ? 1 : 0);
                                        const leftCount = base + (extra > 2 ? 1 : 0);
                                        const rightCount = base;

                                        if (topCount > 0) {
                                            const totalWidth = s.width * 0.8;
                                            const seatWidth = topCount === 1 ? s.width * 0.8 : (totalWidth - spacing * (topCount - 1)) / topCount;
                                            for (let i = 0; i < topCount; i++) {
                                                const left = s.width * 0.1 + i * (seatWidth + spacing);
                                                chairs.push(makeChair("top-" + i, left, -chairThickness - gap, seatWidth, chairThickness));
                                            }
                                        }

                                        if (bottomCount > 0) {
                                            const totalWidth = s.width * 0.8;
                                            const seatWidth = bottomCount === 1 ? s.width * 0.8 : (totalWidth - spacing * (bottomCount - 1)) / bottomCount;
                                            for (let i = 0; i < bottomCount; i++) {
                                                const left = s.width * 0.1 + i * (seatWidth + spacing);
                                                chairs.push(makeChair("bottom-" + i, left, s.height + gap, seatWidth, chairThickness));
                                            }
                                        }

                                        if (leftCount > 0) {
                                            const totalHeight = s.height * 0.8;
                                            const seatHeight = leftCount === 1 ? s.height * 0.8 : (totalHeight - spacing * (leftCount - 1)) / leftCount;
                                            for (let i = 0; i < leftCount; i++) {
                                                const top = s.height * 0.1 + i * (seatHeight + spacing);
                                                chairs.push(makeChair("left-" + i, -chairThickness - gap, top, chairThickness, seatHeight));
                                            }
                                        }

                                        if (rightCount > 0) {
                                            const totalHeight = s.height * 0.8;
                                            const seatHeight = rightCount === 1 ? s.height * 0.8 : (totalHeight - spacing * (rightCount - 1)) / rightCount;
                                            for (let i = 0; i < rightCount; i++) {
                                                const top = s.height * 0.1 + i * (seatHeight + spacing);
                                                chairs.push(makeChair("right-" + i, s.width + gap, top, chairThickness, seatHeight));
                                            }
                                        }

                                        return chairs;
                                    })()
                                )}
                            </>
                        )}

                        {s.type === "image" && (
                            <div
                                className="w-full h-full cursor-pointer"
                                onClick={(e) => handleImageClick(s.id, e)}
                            >
                                {(s as ImageShape).component}
                            </div>
                        )}
                    </div>
                ))}

                {drawing && start && current && (
                    <div
                        className="absolute border-dashed-red"
                        style={{
                            left: Math.min(start.x, current.x),
                            top: Math.min(start.y, current.y),
                            width: Math.abs(current.x - start.x),
                            height: Math.abs(current.y - start.y),
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default DrawFloorPlan;